// src/pages/LearnerPortfolio.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import PortfolioItemCard from '../components/learner/PortfolioItemCard';
import '../styles/portfolio.css';

const LearnerPortfolio = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPortfolioData = async () => {
      if (!currentUser) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const db = getFirestore();
        const today = new Date();

        const assignmentDocs = await getDocs(
          query(
            collection(db, "assignments"),
            where("endDate", "<", today),
            orderBy("endDate", "desc")
          )
        );

        setAssignments(assignmentDocs.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startDate: doc.data().startDate?.toDate?.() || doc.data().startDate,
          endDate: doc.data().endDate?.toDate?.() || doc.data().endDate
        })));

        const submissionDocs = await getDocs(
          query(
            collection(db, "submissions"),
            where("learnerId", "==", currentUser.uid),
            where("submissionStatus", "in", ["submitted", "updated"]),
            orderBy("updatedAt", "desc")
          )
        );

        setSubmissions(submissionDocs.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
        })));
      } catch (err) {
        console.error("Error fetching portfolio data:", err);
        setError("Failed to load portfolio data");
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [currentUser]);

  const portfolioItems = (() => {
    const assignmentMap = assignments.reduce((map, a) => { map[a.id] = a; return map; }, {});
    return submissions
      .filter(s => assignmentMap[s.assignmentId])
      .map(s => ({ submission: s, assignment: assignmentMap[s.assignmentId] }));
  })();

  const handleItemClick = (item) => {
    navigate(`/portfolio/${item.assignment.id}`);
  };

  if (loading) {
    return (
      <div className="portfolio-page">
        <div className="portfolio-header">
          <h1>My Portfolio</h1>
          <p>View your completed assignments</p>
        </div>
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-page">
        <div className="portfolio-header">
          <h1>My Portfolio</h1>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <a href="/learner" className="portfolio-button">Back to Dashboard</a>
        </div>
      </div>
    );
  }

  if (portfolioItems.length === 0) {
    return (
      <div className="portfolio-page">
        <div className="portfolio-header">
          <h1>My Portfolio</h1>
          <p>View your completed assignments</p>
        </div>
        <div className="empty-portfolio-container">
          <div className="empty-portfolio">
            <h2>Your Portfolio is Empty</h2>
            <p>Your completed assignments will appear here once you have submitted them and the due date has passed.</p>
            <a href="/learner" className="portfolio-button">Back to Assignments</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <h1>My Portfolio</h1>
        <p>View your completed assignments</p>
      </div>

      <div className="portfolio-list">
        {portfolioItems.map(item => (
          <PortfolioItemCard
            key={`${item.assignment.id}-${item.submission.id}`}
            assignment={item.assignment}
            submission={item.submission}
            onClick={handleItemClick}
          />
        ))}
      </div>
    </div>
  );
};

export default LearnerPortfolio;